import { useState } from 'react';

import { sendIndirectTaxFilingApprovalMail } from '@commenda/api/indirect-tax/filings';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@commenda/kakashi';

interface IndirectTaxFilingsApprovalMailModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  filingIds: string[];
  onSent: (skippedCount: number) => void;
  onError: (message: string) => void;
}

const IndirectTaxFilingsApprovalMailModal = ({
  isOpen,
  onClose,
  corporationId,
  filingIds,
  onSent,
  onError,
}: IndirectTaxFilingsApprovalMailModalProps) => {
  const [recipients, setRecipients] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    const parsedRecipients = recipients
      .split(/[,\s]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (parsedRecipients.length === 0 || filingIds.length === 0) return;

    setIsSending(true);
    const result = await sendIndirectTaxFilingApprovalMail({
      params: {
        corporationId,
        filingIds,
        recipients: parsedRecipients,
      },
    });
    setIsSending(false);

    if (!result.success || !result.data) {
      onError(result.error ?? 'Failed to send approval request');
      return;
    }

    onSent(result.data.skippedFilingIds.length);
    setRecipients('');
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => (open ? undefined : onClose())}
    >
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle>Send approval request</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-lg p-3xl">
          <p className="txt-small-regular text-text-secondary">
            Send an approval request for {filingIds.length}{' '}
            {filingIds.length === 1 ? 'selected filing' : 'selected filings'}.
            Only filings currently waiting for client approval will be sent.
          </p>
          <div className="flex flex-col gap-xs">
            <label className="txt-small-bold text-text-secondary">
              Recipients
            </label>
            <Input
              value={recipients}
              onChange={(event) => setRecipients(event.target.value)}
              placeholder="customer@example.com, other@example.com"
              disabled={isSending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={isSending || recipients.trim().length === 0}
            onClick={handleSend}
          >
            {isSending ? 'Sending…' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxFilingsApprovalMailModal };
